import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Gavel as GavelIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
  Notes as NotesIcon,
  AccountBalance as AccountBalanceIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import disputeAdminService from '../../services/disputeAdminService';
import Admin3DSManagementPage from './Admin3DSManagementPage';
import AdminCashSettlementPage from './AdminCashSettlementPage';

const STATUS_OPTIONS = ['pending', 'in_progress', 'resolved', 'closed', 'escalated'];

const getStatusColor = (status) => {
  const colors = {
    pending: 'warning',
    in_progress: 'info',
    resolved: 'success',
    closed: 'default',
    escalated: 'error'
  };
  return colors[status] || 'default';
};

const AdminDisputesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('disputes');
  
  // Charger l'onglet mémorisé depuis localStorage ou utiliser 0 par défaut
  const [tabValue, setTabValue] = useState(() => {
    const savedTab = localStorage.getItem('adminDisputesTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });

  // Mémoriser l'onglet actif dans localStorage quand il change
  useEffect(() => {
    localStorage.setItem('adminDisputesTab', tabValue.toString());
  }, [tabValue]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    limit: 10
  });

  // Actions menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDispute, setSelectedDispute] = useState(null);

  // Dialog states
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'start', 'resolve', 'escalate', 'close'
  const [actionNote, setActionNote] = useState('');
  const [compensationAmount, setCompensationAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      const response = await disputeAdminService.list({
        page,
        limit: filters.limit,
        status: filters.status || undefined,
        search: filters.search || undefined
      });
      
      setDisputes(response.data);
      setTotalPages(Math.ceil(response.total / filters.limit));
    } catch (err) {
      console.error('Failed to fetch disputes:', err);
      setError(t('list.error', 'Erreur lors du chargement des litiges'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDisputes();
  }, [page, filters.status]); // Reload on page or status change

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchDisputes();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1); // Reset pagination when changing tabs
  };

  const handleOpenMenu = (event, dispute) => {
    setAnchorEl(event.currentTarget);
    setSelectedDispute(dispute);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenActionDialog = (type) => {
    setActionType(type);
    setActionDialogOpen(true);
    handleCloseMenu();
  };

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setActionNote('');
    setCompensationAmount('');
    setActionType(null);
  };

  const handleExecuteAction = async () => {
    if (!selectedDispute) return;

    try {
      setProcessing(true);

      let statusUpdate = {};

      switch (actionType) {
        case 'start':
          statusUpdate = {
            status: 'in_progress',
            message: actionNote || 'Litige pris en charge par l\'administration'
          };
          break;

        case 'resolve':
          statusUpdate = {
            status: 'resolved',
            resolution: actionNote || 'Litige résolu par l\'administration',
            compensation_amount: compensationAmount ? parseFloat(compensationAmount) : 0,
            message: actionNote
          };
          break;

        case 'escalate':
          statusUpdate = {
            status: 'escalated',
            escalation_level: 'admin',
            message: actionNote || 'Litige escaladé pour traitement prioritaire'
          };
          break;

        case 'close':
          statusUpdate = {
            status: 'closed',
            message: actionNote || 'Litige fermé sans suite'
          };
          break;

        default:
          break;
      }

      await disputeAdminService.updateStatus(selectedDispute.dispute_id, statusUpdate);
      
      setSuccessMessage(`Action "${actionType}" exécutée avec succès !`);
      handleCloseActionDialog();
      fetchDisputes(); // Reload list

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      console.error('Failed to execute action:', err);
      setError(err?.response?.data?.error || 'Erreur lors de l\'exécution de l\'action');
    } finally {
      setProcessing(false);
    }
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'start': return <PlayArrowIcon fontSize="small" />;
      case 'resolve': return <CheckCircleIcon fontSize="small" />;
      case 'escalate': return <TrendingUpIcon fontSize="small" />;
      case 'close': return <CloseIcon fontSize="small" />;
      default: return null;
    }
  };

  const getActionColor = (type) => {
    switch (type) {
      case 'start': return 'info.main';
      case 'resolve': return 'success.main';
      case 'escalate': return 'warning.main';
      case 'close': return 'grey.600';
      default: return 'primary.main';
    }
  };

  const getActionTitle = (type) => {
    switch (type) {
      case 'start': return 'Prendre en charge';
      case 'resolve': return 'Résoudre le litige';
      case 'escalate': return 'Escalader (Prioritaire)';
      case 'close': return 'Fermer sans suite';
      default: return '';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GavelIcon fontSize="large" color="primary" />
          {t('list.title', 'Gestion des Litiges')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('list.subtitle', 'Consultez et gérez les litiges entre utilisateurs. Prenez des actions directement depuis cette interface.')}
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="dispute tabs">
          <Tab label="Tous les litiges" id="tab-0" aria-controls="tabpanel-0" />
          <Tab
            label="Suivi 3DS"
            id="tab-1"
            aria-controls="tabpanel-1"
            icon={<ScheduleIcon />}
            iconPosition="start"
          />
          <Tab
            label="Règlements Espèces"
            id="tab-2"
            aria-controls="tabpanel-2"
            icon={<AccountBalanceIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 ? (
        <>
          {/* Filters Bar - Only for All Disputes tab */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <form onSubmit={handleSearch}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <TextField
                  label={t('filters.search', 'Rechercher (ID, Titre...)')}
                  variant="outlined"
                  size="small"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1 }}
                />
                
                <TextField
                  select
                  label={t('filters.status', 'Statut')}
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value });
                    setPage(1);
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">{t('filters.allStatuses', 'Tous les statuts')}</MenuItem>
                  {STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {t(`status.${status}`, status)}
                    </MenuItem>
                  ))}
                </TextField>

                <Button 
                  type="submit" 
                  variant="contained" 
                  startIcon={<FilterListIcon />}
                >
                  {t('common.filter', 'Filtrer')}
                </Button>
              </Stack>
            </form>
          </Paper>

          {/* All Disputes Table */}
      <Paper elevation={2}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : disputes.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('list.empty', 'Aucun litige trouvé.')}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>{t('list.columns.date', 'Date')}</TableCell>
                    <TableCell>{t('list.columns.title', 'Titre')}</TableCell>
                    <TableCell>{t('list.columns.parties', 'Parties')}</TableCell>
                    <TableCell>{t('list.columns.amount', 'Montant')}</TableCell>
                    <TableCell>{t('list.columns.status', 'Statut')}</TableCell>
                    <TableCell align="right">{t('list.columns.actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {disputes.map((dispute) => (
                    <TableRow key={dispute.dispute_id} hover>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">
                          #{dispute.dispute_id.substring(0, 8)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {format(new Date(dispute.created_at), 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {dispute.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dispute.category_key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="caption">
                            <strong>Rep:</strong> {dispute.reporter?.name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption">
                            <strong>Acc:</strong> {dispute.reportedUser?.name || 'Unknown'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {dispute.compensation_amount ? (
                          <Typography fontWeight="bold" color="error.main">
                            {dispute.compensation_amount} €
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={t(`status.${dispute.status}`, dispute.status)} 
                          color={getStatusColor(dispute.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Voir les détails dans un onglet">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => window.open(`/disputes/${dispute.dispute_id}`, '_blank', 'noopener,noreferrer')}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Actions rapides">
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, dispute)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={(e, p) => setPage(p)} 
                color="primary" 
              />
            </Box>
          </>
        )}
      </Paper>
        </>
      ) : tabValue === 1 ? (
        // Tab 1: 3DS Management
        <Admin3DSManagementPage />
      ) : (
        // Tab 2: Cash Settlements
        <AdminCashSettlementPage />
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {selectedDispute?.status === 'pending' && (
          <MenuItem onClick={() => handleOpenActionDialog('start')}>
            <ListItemIcon>
              <PlayArrowIcon fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText>Prendre en charge</ListItemText>
          </MenuItem>
        )}

        {(selectedDispute?.status === 'pending' || selectedDispute?.status === 'in_progress') && (
          <MenuItem onClick={() => handleOpenActionDialog('resolve')}>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Résoudre</ListItemText>
          </MenuItem>
        )}

        {(selectedDispute?.status === 'pending' || selectedDispute?.status === 'in_progress') && (
          <MenuItem onClick={() => handleOpenActionDialog('escalate')}>
            <ListItemIcon>
              <TrendingUpIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Escalader</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={() => handleOpenActionDialog('close')}>
          <ListItemIcon>
            <CloseIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Fermer sans suite</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => navigate(`/disputes/${selectedDispute?.dispute_id}`)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Voir détails complets</ListItemText>
        </MenuItem>
      </Menu>

      {/* Action Dialog */}
      <Dialog 
        open={actionDialogOpen} 
        onClose={handleCloseActionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getActionIcon(actionType)}
          {getActionTitle(actionType)}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
            Litige : <strong>{selectedDispute?.title}</strong>
          </Typography>

          {actionType === 'resolve' && (
            <TextField
              fullWidth
              label="Montant de compensation"
              type="number"
              value={compensationAmount}
              onChange={(e) => setCompensationAmount(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
              helperText="Montant qui sera prélevé sur la caution (0 = remboursement complet)"
            />
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            label={actionType === 'resolve' ? 'Résolution / Note' : 'Note / Message'}
            value={actionNote}
            onChange={(e) => setActionNote(e.target.value)}
            placeholder={
              actionType === 'start' ? 'Note interne (optionnel)...' :
              actionType === 'resolve' ? 'Expliquez la résolution...' :
              actionType === 'escalate' ? 'Raison de l\'escalade...' :
              'Raison de la fermeture...'
            }
            required={actionType === 'resolve'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActionDialog} disabled={processing}>
            Annuler
          </Button>
          <Button
            onClick={handleExecuteAction}
            variant="contained"
            disabled={processing || (actionType === 'resolve' && !actionNote)}
            sx={{ 
              bgcolor: getActionColor(actionType),
              '&:hover': { 
                bgcolor: getActionColor(actionType),
                opacity: 0.8
              }
            }}
          >
            {processing ? <CircularProgress size={20} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDisputesPage;
